/**
 * Module #34 Phase A chunk 6 — memory-management hook for /admin/copilot/memory.
 *
 * Wraps GET /v1/copilot/memory (active + archived) + POST :id/archive +
 * POST :id/restore + DELETE :id. Mirrors useCopilotConversations: snake→camel
 * mapping, {kind:'idle'|'loading'|'error'} status, optimistic local updates.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession } from './useSession';
import { fetchWithAuth } from '../lib/accessToken';

export interface CopilotMemoryItem {
  memoryId: number;
  kind: string;
  scope: 'user' | 'tenant';
  subject: string | null;
  text: string;
  confidence: number | null;
  archived: boolean;
  archivedReason: string | null;
  createdAt: string;
  lastUsedAt: string;
}

export type MemoryListStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; detail: string };

export interface UseCopilotMemoryReturn {
  memories: CopilotMemoryItem[];
  status: MemoryListStatus;
  refresh: (opts?: { silent?: boolean }) => Promise<void>;
  /** Soft-delete (recoverable). Flips the row's archived flag on success. */
  archive: (id: number) => Promise<boolean>;
  /** Un-archive. Flips the row's archived flag back on success. */
  restore: (id: number) => Promise<boolean>;
  /** Permanent hard-delete. Removes the row on success. */
  remove: (id: number) => Promise<boolean>;
}

interface MemoryApiRow {
  memory_id: number;
  kind: string;
  scope: 'user' | 'tenant';
  subject: string | null;
  text: string;
  confidence: number | null;
  archived: boolean;
  archived_reason: string | null;
  created_at: string;
  last_used_at: string;
}

interface ListApiResponse {
  memories: MemoryApiRow[];
  count: number;
}

interface ApiError {
  error: string;
  detail: string;
}

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

export function useCopilotMemory(): UseCopilotMemoryReturn {
  const { userId } = useSession();
  const base = useMemo(() => ieBaseUrl(), []);
  const [memories, setMemories] = useState<CopilotMemoryItem[]>([]);
  const [status, setStatus] = useState<MemoryListStatus>({ kind: 'idle' });

  const refresh = useCallback(
    async (opts?: { silent?: boolean }): Promise<void> => {
      if (base === null || userId === null) {
        setStatus({ kind: 'idle' });
        setMemories([]);
        return;
      }
      if (opts?.silent !== true) setStatus({ kind: 'loading' });
      let res: Response;
      try {
        res = await fetchWithAuth(`${base}/v1/copilot/memory?include_archived=true`);
      } catch (err) {
        setStatus({ kind: 'error', detail: err instanceof Error ? err.message : 'Network error' });
        return;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as ApiError | null;
        setStatus({ kind: 'error', detail: body?.detail ?? `HTTP ${res.status}` });
        return;
      }
      const data = (await res.json()) as ListApiResponse;
      setMemories(
        data.memories.map((m) => ({
          memoryId: m.memory_id,
          kind: m.kind,
          scope: m.scope,
          subject: m.subject,
          text: m.text,
          confidence: m.confidence,
          archived: m.archived,
          archivedReason: m.archived_reason,
          createdAt: m.created_at,
          lastUsedAt: m.last_used_at,
        })),
      );
      setStatus({ kind: 'idle' });
    },
    [base, userId],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const post = useCallback(
    async (id: number, action: 'archive' | 'restore'): Promise<boolean> => {
      if (base === null || userId === null) return false;
      let res: Response;
      try {
        res = await fetchWithAuth(
          `${base}/v1/copilot/memory/${encodeURIComponent(String(id))}/${action}`,
          {
            method: 'POST',
          },
        );
      } catch {
        return false;
      }
      if (!res.ok) return false;
      const nowArchived = action === 'archive';
      setMemories((prev) =>
        prev.map((m) => (m.memoryId === id ? { ...m, archived: nowArchived } : m)),
      );
      return true;
    },
    [base, userId],
  );

  const archive = useCallback((id: number) => post(id, 'archive'), [post]);
  const restore = useCallback((id: number) => post(id, 'restore'), [post]);

  const remove = useCallback(
    async (id: number): Promise<boolean> => {
      if (base === null || userId === null) return false;
      let res: Response;
      try {
        res = await fetchWithAuth(`${base}/v1/copilot/memory/${encodeURIComponent(String(id))}`, {
          method: 'DELETE',
        });
      } catch {
        return false;
      }
      if (!res.ok) return false;
      setMemories((prev) => prev.filter((m) => m.memoryId !== id));
      return true;
    },
    [base, userId],
  );

  return { memories, status, refresh, archive, restore, remove };
}
